export default function CarryingCostsBreakdown() {
  const costs = [
    { category: 'Mortgage Payment', monthly: '$4,200', annual: '$50,400', description: 'Principal and interest on existing loan' },
    { category: 'Property Taxes', monthly: '$2,100', annual: '$25,200', description: 'Annual tax bill divided monthly' },
    { category: 'Insurance', monthly: '$850', annual: '$10,200', description: 'Property and liability coverage' },
    { category: 'Utilities', monthly: '$650', annual: '$7,800', description: 'Common area electricity, water, gas' },
    { category: 'Maintenance', monthly: '$1,200', annual: '$14,400', description: 'Routine repairs and upkeep' },
    { category: 'Property Management', monthly: '$0', annual: '$0', description: 'Your time and stress (unpaid)' },
    { category: 'Legal/Probate Fees', monthly: '$500', annual: '$6,000', description: 'Ongoing attorney and court costs' }
  ];

  const totalMonthly = costs.reduce((sum, item) => {
    const amount = parseFloat(item.monthly.replace(/[$,]/g, ''));
    return sum + amount;
  }, 0);

  const totalAnnual = costs.reduce((sum, item) => {
    const amount = parseFloat(item.annual.replace(/[$,]/g, ''));
    return sum + amount;
  }, 0);

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            The True Cost of Holding an Inherited Apartment Building
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Every month you hold the property, these costs accumulate. Here's a realistic breakdown for a typical 12-unit inherited apartment building.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-lg font-semibold">Expense Category</th>
                  <th className="px-6 py-4 text-right text-lg font-semibold">Monthly</th>
                  <th className="px-6 py-4 text-right text-lg font-semibold">Annual</th>
                  <th className="px-6 py-4 text-left text-lg font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                {costs.map((item, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-semibold text-gray-900">{item.category}</td>
                    <td className="px-6 py-4 text-right font-bold text-gray-900">{item.monthly}</td>
                    <td className="px-6 py-4 text-right font-bold text-gray-900">{item.annual}</td>
                    <td className="px-6 py-4 text-gray-600">{item.description}</td>
                  </tr>
                ))}
                <tr className="bg-teal-50 font-bold text-lg">
                  <td className="px-6 py-5 text-gray-900">TOTAL CARRYING COSTS</td>
                  <td className="px-6 py-5 text-right text-teal-600">${totalMonthly.toLocaleString()}</td>
                  <td className="px-6 py-5 text-right text-teal-600">${totalAnnual.toLocaleString()}</td>
                  <td className="px-6 py-5 text-gray-700">Plus your unpaid time and stress</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <div className="text-5xl font-bold text-red-600 mb-3">${(totalMonthly * 6).toLocaleString()}</div>
            <p className="text-gray-700 font-semibold mb-2">6-Month Cost</p>
            <p className="text-sm text-gray-600">Minimum probate timeline</p>
          </div>
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <div className="text-5xl font-bold text-red-600 mb-3">${(totalMonthly * 12).toLocaleString()}</div>
            <p className="text-gray-700 font-semibold mb-2">12-Month Cost</p>
            <p className="text-sm text-gray-600">Average probate timeline</p>
          </div>
          <div className="bg-white rounded-xl p-8 shadow-lg text-center">
            <div className="text-5xl font-bold text-red-600 mb-3">${(totalMonthly * 18).toLocaleString()}</div>
            <p className="text-gray-700 font-semibold mb-2">18-Month Cost</p>
            <p className="text-sm text-gray-600">Extended probate timeline</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl p-10 text-white">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full flex-shrink-0">
              <i className="ri-alarm-warning-line text-3xl"></i>
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-4">Don't Forget Hidden Costs</h3>
              <p className="text-xl text-white/90 mb-4 leading-relaxed">
                Beyond these monthly expenses, inherited properties often come with surprise costs:
              </p>
              <ul className="space-y-3 text-lg">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90"><strong>Emergency repairs:</strong> $5,000-$25,000 for roof leaks, plumbing failures, HVAC breakdowns</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90"><strong>Code violations:</strong> $10,000-$50,000 to bring property up to current standards</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90"><strong>Tenant issues:</strong> Eviction costs, unpaid rent, property damage</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-circle-fill text-white text-xl flex-shrink-0 mt-1"></i>
                  <span className="text-white/90"><strong>Travel expenses:</strong> Flights, hotels, meals for out-of-state heirs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
